/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */

const NONE = 0;

/**
 * Log is a minimal, dependency-free logger intended for both console and in-page diagnostics.
 */
class Log {
    static _displayTimeStamp = false;
    static _maxDisplayLevel = NONE;
    static _htmlElement = null;
    static _autoScroll = false;
    static _lastLogWasProgress = false;
    static raw =false;

    /**
     * Enables logging with the given level and options.
     * @param {number} level
     * @param {HTMLElement|null} element
     * @param {boolean} displayTimeStamp
     * @param {boolean} scrollTop
     */
    static enable(level = Log.Warning, element = null, displayTimeStamp = true, scrollTop = false) {
        Log._maxDisplayLevel = level;
        Log._htmlElement = element;
        Log._displayTimeStamp = displayTimeStamp;
        Log._autoScroll = scrollTop;
        if (!Log._htmlElement) {
            console.log(`Log enabled with max level="${Log.name(level)}"`);
        }
    }

    static None = 0;
    static Progress = 1; // Progress is intentionally single-line to reduce DOM churn and keep status UIs readable.
    static Info = 2;
    static Error = 3;
    static Warning = 4;
    static Dev = 50;
    static Core = 100;
    static All = 1000;

    static clear(){
        if (Log._htmlElement) {
            Log._htmlElement.innerHTML = '';
        }
    }

    static enableRaw(){
        Log.raw = true;
    }
    static disableRaw(){
        Log.raw = false;
    }

    static info(...messages) {
        Log.display(Log.Info, ...messages);
    }

    static progress(...messages) {
        Log.display(Log.Progress, ...messages);
    }

    static error(...messages) {
        Log.display(Log.Error, ...messages);
    }

    static warn(...messages) {
        Log.display(Log.Warning, ...messages);
    }

    static dev(...messages) {
        Log.display(Log.Dev, ...messages);
    }

    static core(...messages) {
        Log.display(Log.Core, ...messages);
    }

    static supports(level) {
        return level <= Log._maxDisplayLevel;
    }

    /**
     * Progress messages overwrite the previous progress line (when applicable) to keep output concise in UI contexts.
     * @param {number} level
     * @param {...string} messages
     */
    static display(level, ...messages) {
        if (level > Log._maxDisplayLevel) return;

        const levelName = Log.name(level);
        const timestamp = Log._displayTimeStamp ? Log.getTimeStamp() : '';
        const fullMessage = Log.raw ? messages.join(' '):`${timestamp}${levelName} ${messages.join(' ')}`;

        if (Log._htmlElement) {
            if (level === Log.Progress && Log._lastLogWasProgress) {
                const parts = Log._htmlElement.innerHTML.split('<br>');
                parts.pop();
                Log._htmlElement.innerHTML = parts.join('<br>');
            }
            Log._htmlElement.innerHTML += (Log._htmlElement.innerHTML ? '<br>' : '') + fullMessage;

            if (Log._autoScroll) {
                Log._htmlElement.scrollTop = Log._htmlElement.scrollHeight;
            }
        } else {
            console.log(fullMessage);
        }
        Log._lastLogWasProgress = (level === Log.Progress);
    }

    /**
     * Timestamp is based on performance.now() to avoid clock jumps affecting log ordering.
     * Format "sssss.mmm: "
     */
    static getTimeStamp() {
        const now = performance.now();
        const secs = Math.floor(now / 1000).toString().padStart(4, '0');
        const millis = Math.floor(now % 1000).toString().padStart(3, '0');
        return `${secs}.${millis}: `;
    }

    /**
     * @param {number} level
     */
    static name(level) {
        switch (level) {
            case Log.None:
                return 'None';
            case Log.Info:
                return 'Info';
            case Log.Progress:
                return 'Progress';
            case Log.Error:
                return 'Error';
            case Log.Warning:
                return 'Warning';
            case Log.Dev:
                return 'Dev';
            case Log.Core:
                return 'Core';
            case Log.All:
                return 'All';
            default:
                return 'Unknown';
        }
    }
}

export {Log};